import { useState } from "react";
import { Button } from "@/components/ui/button";
import { defaultButtonClasses } from "@/constants/ui";
import RoomSelector from "../RoomSelector";
import { useChangeBookingFlow } from "./ChangeBookingFlowProvider";
import type { Room as RoomType } from "@/types/Room";

type RoomProps = {
  onClose?: () => void;
};

const Room = ({ onClose }: RoomProps) => {
  const { booking, resetFlow, navigateTo, setNewRoom } = useChangeBookingFlow();
  const [selectedRoom, setSelectedRoom] = useState<RoomType | null>(null);
  const [hasConflict, setHasConflict] = useState(false);

  const handleRoomChange = (newRoom: RoomType | null, hasConflict?: boolean) => {
    console.log("Room changed:", { newRoom, hasConflict });
    setSelectedRoom(newRoom);
    setHasConflict(hasConflict ?? false);
  };

  const handleNext = () => {
    if (selectedRoom && !hasConflict) {
      // Save the new room to the flow state
      setNewRoom(selectedRoom);
      // Move to confirmation step
      navigateTo("room-confirmation");
    }
  };

  const handleCancel = () => {
    resetFlow();
    onClose?.();
  };

  if (!booking) {
    return (
      <div className="p-6">
        <p className="text-red-600">Error: No booking data available</p>
        <Button onClick={handleCancel} className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="max-h-[calc(100vh-2rem)] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50">
        <h2 className="text-2xl font-semibold mb-2">Change Room</h2>
        <p className="text-gray-600 text-xs">Browse available rooms and select a different room for your booking.</p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">Change Room</h2>
          <p className="mb-2 text-gray-600 text-sm">
            Use the room navigation to browse available rooms. Your booking time will remain the same, only the room
            will change.
          </p>
          <div className="mt-4 space-y-2">
            <div className="p-2 bg-white rounded border border-gray-200">
              <p className="text-xs font-semibold text-gray-700">Current Booking:</p>
              <p className="text-sm text-gray-600">
                {booking.date.toDateString()} at {booking.startTime} - {booking.endTime}
              </p>
              <p className="text-sm text-gray-600 mt-1">Room: {booking.room?.name}</p>
            </div>
          </div>
        </div>

        {/* Right column - Room Selector */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm flex flex-col max-h-[calc(100vh-6rem)]">
          <div className="flex-1 overflow-y-auto min-h-0">
            <RoomSelector
              date={booking.date}
              booking={booking}
              location={booking.location ?? null}
              room={booking.room ?? null}
              hoursPadding={1}
              onRoomChange={handleRoomChange}
            />
          </div>
          <div className="flex flex-col gap-2 mt-6 flex-shrink-0">
            <Button
              className={`w-full ` + defaultButtonClasses}
              onClick={handleNext}
              disabled={!selectedRoom || hasConflict}
            >
              Next
            </Button>
            <Button variant="ghost" className="w-full text-gray-600" onClick={handleCancel}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto mb-6">
          <RoomSelector
            date={booking.date}
            booking={booking}
            location={booking.location ?? null}
            room={booking.room ?? null}
            hoursPadding={1}
            onRoomChange={handleRoomChange}
          />
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Button
            className={`w-full ` + defaultButtonClasses}
            onClick={handleNext}
            disabled={!selectedRoom || hasConflict}
          >
            Next
          </Button>
          <Button variant="ghost" className="w-full text-gray-600" onClick={handleCancel}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Room;
